import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiCalendar, FiUser, FiEye, FiClock, FiTag, FiShare2 } from 'react-icons/fi';
import { getBlogBySlug } from '../services/api';

const Container = styled.div`
  padding: 80px 20px;
  max-width: 800px;
  margin: 0 auto;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  margin-bottom: 2rem;
  transition: color 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
  }
`;

const Header = styled.div`
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Excerpt = styled.p`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const FeaturedImage = styled.div`
  width: 100%;
  height: 400px;
  background: ${props => props.image ? `url(${props.image})` : props.theme.colors.background};
  background-size: cover;
  background-position: center;
  border-radius: 12px;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    height: 250px;
  }
`;

const MetaSection = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.colors.border};
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.9rem;
`;

const TagsSection = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.primary};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.3rem;
`;

const Content = styled.div`
  font-size: 1.1rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.text};
  
  h1, h2, h3, h4, h5, h6 {
    color: ${props => props.theme.colors.primary};
    margin-top: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 2rem;
  }
  
  li {
    margin-bottom: 0.5rem;
  }
  
  blockquote {
    border-left: 4px solid ${props => props.theme.colors.primary};
    padding-left: 1rem;
    margin: 2rem 0;
    font-style: italic;
    color: ${props => props.theme.colors.textSecondary};
  }
  
  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    margin: 1.5rem 0;
  }
  
  a {
    color: ${props => props.theme.colors.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const ShareSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const ShareButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  &:hover {
    background: ${props => props.theme.colors.primaryDark};
  }
`;

const LoadingContainer = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.error};
`;

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getBlogBySlug(slug);
        setBlog(data);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Blog not found or no longer available.');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlog();
    }
  }, [slug]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      } catch (error) {
        console.log('Error copying to clipboard:', error);
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingContainer>Loading blog post...</LoadingContainer>
      </Container>
    );
  }

  if (error || !blog) {
    return (
      <Container>
        <ErrorContainer>
          <h2>Blog Not Found</h2>
          <p>{error || 'The blog post you are looking for does not exist.'}</p>
          <BackButton to="/blog">
            <FiArrowLeft />
            Back to Blog
          </BackButton>
        </ErrorContainer>
      </Container>
    );
  }

  return (
    <Container>
      <BackButton to="/blog">
        <FiArrowLeft />
        Back to Blog
      </BackButton>

      <Header>
        <Title>{blog.title}</Title>
        <Excerpt>{blog.excerpt}</Excerpt>
        
        {blog.featuredImage && (
          <FeaturedImage image={blog.featuredImage} />
        )}
        
        <MetaSection>
          <MetaItem>
            <FiUser />
            {blog.author}
          </MetaItem>
          <MetaItem>
            <FiCalendar />
            {formatDate(blog.publishedAt || blog.createdAt)}
          </MetaItem>
          <MetaItem>
            <FiClock />
            {blog.readTime || 5} min read
          </MetaItem>
          {blog.views > 0 && (
            <MetaItem>
              <FiEye />
              {blog.views} views
            </MetaItem>
          )}
        </MetaSection>
        
        {blog.tags && blog.tags.length > 0 && (
          <TagsSection>
            {blog.tags.map((tag, index) => (
              <Tag key={index}>
                <FiTag />
                {tag}
              </Tag>
            ))}
          </TagsSection>
        )}
      </Header>

      <Content 
        dangerouslySetInnerHTML={{ __html: blog.content }}
      />

      <ShareSection>
        <ShareButton onClick={handleShare}>
          <FiShare2 />
          Share this post
        </ShareButton>
      </ShareSection>
    </Container>
  );
};

export default BlogDetailPage; 